import {useEffect, useMemo, useState} from "react";
import ProductCard from "./ProductCard.tsx";
import {IProduct} from "../types.ts";
import {FilterAccordion} from "../shared.ts";
import {useAutoAnimate} from "@formkit/auto-animate/react";
import { useTranslation } from 'react-i18next';
import { useShopifyLanguageSync } from '../../../i18n/useShopifyLanguageSync';

export default function ProductsPage() {
	const [productsContainer] = useAutoAnimate();
	const { t } = useTranslation(['products', 'filters']);
	useShopifyLanguageSync(); // Sync with Shopify language

	// Settings up base data
	const entreprisesData = window.entreprisesData;
	const isLoggedIn = window.isLoggedIn;

	// Create a mapping from translated values back to database values
	const getDatabaseValueFromTranslated = (translatedValue: string): string | null => {
		const productTypesMap = t('products:productTypes', { returnObjects: true }) as Record<string, string>;

		// Find the database key that maps to this translated value
		for (const [dbValue, translatedValueFromMap] of Object.entries(productTypesMap)) {
			if (translatedValueFromMap === translatedValue) {
				return dbValue;
			}
		}
		return null;
	};

	// Function to translate operation types with fallback for unmapped values
	const translateOperationType = (operationValue: string): string => {
		const operationTypesMap = t('products:operationTypes', { returnObjects: true }) as Record<string, string>;
		return operationTypesMap[operationValue] || operationValue;
	};

	// Function to translate polymer types with fallback for unmapped values
	const translatePolymerType = (polymerValue: string): string => {
		const polymerTypesMap = t('products:polymerTypes', { returnObjects: true }) as Record<string, string>;

		// Keep acronyms as-is (NR, EPDM, SBR, BR, etc.)
		if (polymerValue.match(/^[A-Z]{2,}$/)) {
			return polymerValue;
		}

		return polymerTypesMap[polymerValue] || polymerValue;
	};

	const productTypesMap = t('products:productTypes', { returnObjects: true }) as Record<string, string>;
	const productsTypes: Set<string> = new Set(Object.values(productTypesMap));

	const operationTypes: Set<string> = new Set(
		entreprisesData
			.flatMap((entreprise) =>
				entreprise.type_operation
					? entreprise.type_operation.split(';').filter((operation) => operation !== '')
					: []
			)
			.map(operation => translateOperationType(operation.trim()))
	);

	// Get all polymer types from entreprises for filter population
	const allEntreprisePolymerTypes: Set<string> = new Set(
		entreprisesData
			.flatMap((entreprise) =>
				entreprise.type_polymer
					? entreprise.type_polymer.split(';').filter((polymer) => polymer !== '')
					: []
			)
			.map(polymer => translatePolymerType(polymer.trim()))
	);

	// Also include polymer types from products that might not be in entreprise types
	const allProductPolymerTypes: Set<string> = new Set(
		entreprisesData
			.flatMap((entreprise) => entreprise.products)
			.flatMap((product) =>
				product.polymer_type
					? product.polymer_type.split(';').filter((polymer) => polymer !== '')
					: []
			)
			.map(polymer => translatePolymerType(polymer.trim()))
	);

	// Combine both for complete filter options
	const polymerTypes: Set<string> = new Set([...allEntreprisePolymerTypes, ...allProductPolymerTypes]);

	// Filtering state management
	const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>({
		productTypes: [],
		operationTypes: [],
		polymerTypes: []
	});

	const createFilterHandler = (filterKey: keyof typeof selectedFilters) => {
		return (selectedValues: string[]) => {
			setSelectedFilters(prev => ({
				...prev,
				[filterKey]: selectedValues
			}));
		};
	};

	const handleProductTypeChange = createFilterHandler('productTypes');
	const handleOperationTypeChange = createFilterHandler('operationTypes');
	const handlePolymerTypeChange = createFilterHandler('polymerTypes');

	const [displayProducts, setDisplayProducts] = useState<IProduct[]>(
		entreprisesData.flatMap((entreprise) => entreprise.products)
	);
	const filterProducts = () => {

		// Filter products based on all criteria
		let products: IProduct[] = [];

		for (const entreprise of entreprisesData) {
			for (const product of entreprise.products) {
				let includeProduct = true;

				// Check product type filter
				if (selectedFilters.productTypes.length > 0) {
					const selectedDbValues = Array.from(selectedFilters.productTypes)
						.map(translatedValue => getDatabaseValueFromTranslated(translatedValue))
						.filter(dbValue => dbValue !== null);
					if (!selectedDbValues.includes(product.type)) {
						includeProduct = false;
					}
				}

				// Check operation type filter (based on entreprise)
				if (includeProduct && selectedFilters.operationTypes.length > 0) {
					const entrepriseOperations = entreprise.type_operation
						? entreprise.type_operation.split(';').map(op => translateOperationType(op.trim()))
						: [];
					const hasMatchingOperation = selectedFilters.operationTypes.some(selectedOp =>
						entrepriseOperations.includes(selectedOp)
					);
					if (!hasMatchingOperation) {
						includeProduct = false;
					}
				}

				// Check polymer type filter (based on product)
				if (includeProduct && selectedFilters.polymerTypes.length > 0) {
					const productPolymers = product.polymer_type
						? product.polymer_type.split(';').map(poly => translatePolymerType(poly.trim()))
						: [];
					const hasMatchingPolymer = selectedFilters.polymerTypes.some(selectedPoly =>
						productPolymers.includes(selectedPoly)
					);
					if (!hasMatchingPolymer) {
						includeProduct = false;
					}
				}

				if (includeProduct) {
					products.push(product);
				}
			}
		}

		if (!isLoggedIn) {
			products = products.filter(product => product.visibility === 'true' || product.visibility === '');
		}

		setDisplayProducts(products);
	};

	const productsPerPage: number = 9;
	const [currentPage, setCurrentPage] = useState<number>(1);
	const maxPages: number = useMemo(() => {
		if (displayProducts.length === 0) {
			return 1;
		}
		return Math.ceil(displayProducts.length / productsPerPage);
	}, [displayProducts]);

	useEffect(() => {
		if (currentPage > maxPages) {
			setCurrentPage(maxPages);
		}
	}, [maxPages]);

	useEffect(() => {
		// Apply initial filtering including visibility check on component mount
		filterProducts();
	}, []);

	useEffect(() => {
		// Re-filter products whenever selectedFilters changes
		filterProducts();
	}, [selectedFilters]);

	useEffect(() => {
		window.scrollTo({top: 0, behavior: 'smooth'});
		setTimeout(() => {
			window.scrollTo({top: 0, behavior: 'smooth'});
		}, 10);
	}, [currentPage]);

	return (
		<div className={'wrapper'}>
			<h1 className={'tw-my-16'}>{t('products:title')}</h1>
			<div className={'tw-grid tw-grid-cols-4 tw-gap-10 tw-items-start tw-my-16 grid-list-axial'}>
				<div className="axial-filters">
					<div className={'tw-flex tw-justify-between tw-p-4 tw-bg-white'}>
						<span className={'tw-font-bold'}>{t('filters:title')}</span>
						<span className={'tw-text-[#757575]'}>{displayProducts.length} {t(displayProducts.length === 1 ? 'filters:results' : 'filters:resultsPlural')}</span>
					</div>
					<FilterAccordion name={t('filters:productCategories')} options={Array.from(productsTypes)} onChange={handleProductTypeChange}/>
					<FilterAccordion name={t('filters:operationType')} options={Array.from(operationTypes)} onChange={handleOperationTypeChange}/>
					<FilterAccordion name={t('filters:polymerType')} options={Array.from(polymerTypes)} onChange={handlePolymerTypeChange}/>
				</div>
				<div ref={productsContainer} className="tw-col-span-3 tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-10 list-axial">
					{displayProducts.slice((currentPage - 1) * productsPerPage, currentPage * productsPerPage).map((product) => <ProductCard product={product} key={product.title}/>)}
				</div>
			</div>
			<div className={'pagination'}>
				<button data-current={currentPage === 1 ? 'true' : 'false'} onClick={() => setCurrentPage(1)}>1</button>
				{currentPage > 3 && <span>...</span>}
				{Array.from({length: maxPages - 1}, (_, i) => i + 1)
					.slice(Math.max(1, currentPage - 2), Math.min(maxPages, currentPage + 1))
					.map((page) => <button data-current={currentPage === page ? 'true' : 'false'} onClick={() => setCurrentPage(page)}>{page}</button>
				)}
				{currentPage < maxPages - 2 && <span>...</span>}
				{maxPages >= 2 && <button data-current={currentPage === maxPages ? 'true' : 'false'} onClick={() => setCurrentPage(maxPages)}>{maxPages}</button>}
			</div>
			{/*<div ref={loadMoreProductsObserverRef} />*/}
		</div>
	)
}